import React, { Fragment } from "react";
import {
  faGasPump,
  faMapMarkerAlt,
} from "@fortawesome/pro-solid-svg-icons";
import { faMap } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FuelInstruction, FuelPlanResponse, FuelPlanStop } from "types";
import type { FuelPlanFeedbackLayout } from "types/fuelPlanFeedbackLayout";
import {
  formatMiles,
  getDisplayLeg,
  getPlannedFuelStops,
  SelectedFuelStops,
} from "pages/fuel-plan/fuel-plan-display";
import FuelPlanGuidance from "./fuel-plan-guidance";

type FuelPlanTimelineProps = {
  fuelPlan: FuelPlanResponse;
  selectedFuelStops: SelectedFuelStops;
  selectedCalculatorStop: FuelPlanStop | null;
  onSelectCalculatorLeg: (stop: FuelPlanStop) => void;
  onViewFuelStops: (recommendation: FuelPlanStop) => void;
  onViewPlannedFuelStops: () => void;
  feedback?: (
    renderLayout: FuelPlanFeedbackLayout
  ) => React.ReactNode;
};

const getGuidancePillClass = (instruction: FuelInstruction): string => {
  switch (instruction) {
    case "JustEnough":
      return "fuel-plan-pill-warning";
    case "DoNotFuel":
      return "fuel-plan-pill-muted";
    default:
      return "fuel-plan-pill-success";
  }
};

const FuelPlanTimeline = ({
  fuelPlan,
  selectedFuelStops,
  selectedCalculatorStop,
  onSelectCalculatorLeg,
  onViewFuelStops,
  onViewPlannedFuelStops,
  feedback,
}: FuelPlanTimelineProps) => {
  const hasPlannedFuelStops =
    getPlannedFuelStops(fuelPlan.stops, selectedFuelStops).length > 0;

  const renderHeader = (action?: React.ReactNode) => (
    <div className="fuel-plan-header">
      <div className="fuel-plan-header-copy">
        <h1>Fuel Stop Timeline</h1>
      </div>
      <div className="fuel-plan-header-actions">
        {hasPlannedFuelStops ? (
          <button
            className="fuel-plan-overview-map"
            onClick={onViewPlannedFuelStops}
            type="button"
          >
            <FontAwesomeIcon aria-hidden="true" icon={faMap} />
            Map View
          </button>
        ) : null}
        {action}
      </div>
    </div>
  );

  return (
    <section>
      {feedback ? (
        feedback((action, details) => (
          <>
            {renderHeader(action)}
            {details}
            <FuelPlanGuidance />
          </>
        ))
      ) : (
        <>
          {renderHeader()}
          <FuelPlanGuidance />
        </>
      )}

      <div className="fuel-plan-timeline-list">
        {fuelPlan.stops.map((timelineStop) => {
          const displayedLeg = getDisplayLeg(
            timelineStop,
            fuelPlan.stops,
            selectedFuelStops
          );
          const isCalculatorEligible =
            timelineStop.nextLeg?.isCalculatorEligible ?? false;
          const isCalculatorLegSelected =
            isCalculatorEligible &&
            selectedCalculatorStop?.sequence === timelineStop.sequence;
          const hasFuelStopAction =
            timelineStop.type === "FuelRecommendation" &&
            timelineStop.fuelStops.length > 0;
          const displayedLegContent = displayedLeg ? (
            <Fragment>
              <span className="fuel-plan-leg-copy">
                <strong>
                  {displayedLeg.fromDisplayName} &rarr;{" "}
                  {displayedLeg.toDisplayName}
                </strong>
                <small>
                  {isCalculatorEligible ? "How Much Fuel?" : "Fixed route"}
                </small>
              </span>
              <span className="fuel-plan-leg-miles">
                {formatMiles(displayedLeg.miles)}
              </span>
            </Fragment>
          ) : null;

          return (
            <Fragment key={timelineStop.sequence}>
              <div className="fuel-plan-stop-row">
                <div
                  aria-label={
                    timelineStop.type === "FuelRecommendation"
                      ? "Fuel stop"
                      : "Load stop"
                  }
                  className="fuel-plan-stop-marker"
                  role="img"
                >
                  <FontAwesomeIcon
                    aria-hidden="true"
                    icon={
                      timelineStop.type === "FuelRecommendation"
                        ? faGasPump
                        : faMapMarkerAlt
                    }
                  />
                </div>
                <div className="fuel-plan-stop-card">
                  <div className="fuel-plan-stop-main">
                    <strong>{timelineStop.displayName}</strong>
                    <span>
                      <b>{timelineStop.subtitle}</b>
                      {timelineStop.city || timelineStop.state ? (
                        <small>
                          {[timelineStop.city, timelineStop.state]
                            .filter(Boolean)
                            .join(", ")}
                        </small>
                      ) : null}
                    </span>
                  </div>
                  {hasFuelStopAction ? (
                    <div className="fuel-plan-stop-actions">
                      <button
                        className="fuel-plan-view-stops"
                        onClick={() => onViewFuelStops(timelineStop)}
                        type="button"
                      >
                        View Fuel Stops
                      </button>
                      {timelineStop.guidance ? (
                        <span
                          className={`fuel-plan-pill ${getGuidancePillClass(
                            timelineStop.guidance.code
                          )}`}
                        >
                          {timelineStop.guidance.label}
                        </span>
                      ) : null}
                    </div>
                  ) : timelineStop.guidance ? (
                    <span
                      className={`fuel-plan-pill ${getGuidancePillClass(
                        timelineStop.guidance.code
                      )}`}
                    >
                      {timelineStop.guidance.label}
                    </span>
                  ) : null}
                </div>
              </div>
              {displayedLeg ? (
                isCalculatorEligible ? (
                  <button
                    className={`fuel-plan-leg fuel-plan-leg-actionable ${
                      isCalculatorLegSelected ? "fuel-plan-leg-selected" : ""
                    }`}
                    onClick={() => onSelectCalculatorLeg(timelineStop)}
                    type="button"
                  >
                    {displayedLegContent}
                  </button>
                ) : (
                  <div className="fuel-plan-leg fuel-plan-leg-readonly">
                    {displayedLegContent}
                  </div>
                )
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </section>
  );
};

export default FuelPlanTimeline;
